export const usePayShareFeeCalculation = (feetypes: any[]) => {
  // Helper function to extract course ID safely
  const getCourseId = (studentData: any) =>
    studentData?.course?.id ??
    studentData?.course_id ??
    studentData?.currentEnrollment?.course_id ??
    null;

  // Helper function to extract stage ID safely
  const getStageId = (studentData: any) =>
    studentData?.course?.stage?.id ??
    studentData?.stage_id ??
    studentData?.currentEnrollment?.stage_id ??
    studentData?.selectedStage ??
    null;

  // Helper function to get fee and share information from student data
  const getFeeAndShareInfo = (studentData: any, feeType: string) => {
    if (!studentData || !feeType) {
      return { feeAmount: 0, shareAmount: 0, paymentStatus: "pending", paymentDate: new Date().toISOString().split("T")[0] };
    }

    // Get fee type by ID from API response
    const feeTypeObj = feetypes.find((ft) => ft.id.toString() === feeType);
    const feeTypeCode = feeTypeObj?.type || (feeType === "1" ? "coursefee" : feeType === "2" ? "registrationfee" : "");

    const courseId = getCourseId(studentData);
    const stageId = getStageId(studentData);
    const center = studentData.center;

    const regFeeShareList = center?.reg_fee_share || center?.regFeeShare || [];
    const courseFeeShareList = center?.course_fee_share || center?.courseFeeShare || [];

    // 1. REGISTRATION FEE
    if (feeType === "2" || feeTypeCode === "registrationfee") {
      const regFeeShare = regFeeShareList.find(
        (fee: any) => Number(fee.course_id) === Number(courseId)
      );

      // Find matching invoice if student has one
      const matchingInvoice = studentData.invoices?.find(
        (inv: any) => inv.type === "registrationfee"
      );

      // Check centerPayments for existing payment
      const matchingPayment = studentData.centerPayments?.find(
        (payment: any) =>
          payment.fee_type === "registrationfee" ||
          (matchingInvoice && payment.invoice_id === matchingInvoice.id)
      );

      let feeAmount = 0;
      let shareAmount = 0;
      let shareType: "fixed" | "percent" = regFeeShare?.share_type === "percent" ? "percent" : "fixed";

      if (regFeeShare) {
        feeAmount =
          Number(matchingInvoice?.amount || 0) > 0
            ? Number(matchingInvoice.amount)
            : Number(regFeeShare.reg_fee || 0) > 0
            ? Number(regFeeShare.reg_fee)
            : Number(matchingInvoice?.payable || 0);

        if (regFeeShare.share_type === "fixed") {
          shareAmount = Number(regFeeShare.share_amount || 0);
        } else if (regFeeShare.share_type === "percent") {
          shareAmount = Math.round(feeAmount * (Number(regFeeShare.share_amount || 0) / 100));
        }
      } else if (matchingInvoice) {
        feeAmount = Number(matchingInvoice.amount || matchingInvoice.payable || 0);
        shareAmount = Math.round(feeAmount * 0.5);
      }

      if (matchingPayment) {
        return {
          feeAmount,
          shareAmount: Number(matchingPayment.amount ?? shareAmount),
          shareType,
          paymentStatus: matchingPayment.status || "pending",
          paymentDate: matchingPayment.created_at?.split("T")[0] || new Date().toISOString().split("T")[0],
        };
      }

      return {
        feeAmount,
        shareAmount,
        shareType,
        paymentStatus: "pending",
        paymentDate: new Date().toISOString().split("T")[0],
      };
    }

    // 2. COURSE FEE
    if (feeType === "1" || feeTypeCode === "coursefee") {
      const courseFeeShare = courseFeeShareList.find(
        (fee: any) => Number(fee.course_id) === Number(courseId)
      );

      const stageFeeShareList = courseFeeShare?.stageFeeShare || courseFeeShare?.stage_fee_share || [];
      const stageFeeShare = stageFeeShareList.find((stage: any) =>
        stageId ? Number(stage.stage_id) === Number(stageId) : true
      );

      // Find matching invoice if student has one
      const matchingInvoice = studentData.invoices?.find((inv: any) => {
        if (inv.type !== "coursefee") return false;
        if (stageId && inv.stage_id) {
          return Number(inv.stage_id) === Number(stageId);
        }
        return true;
      });

      // Check centerPayments for existing payment
      const matchingPayment = studentData.centerPayments?.find((payment: any) => {
        if (payment.fee_type !== "coursefee") return false;
        if (matchingInvoice && payment.invoice_id === matchingInvoice.id) return true;
        if (stageId && payment.stage_id) {
          return Number(payment.stage_id) === Number(stageId);
        }
        return true;
      });

      let feeAmount = 0;
      let shareAmount = 0;
      let shareType: "fixed" | "percent" = courseFeeShare?.share_type === "percent" ? "percent" : "fixed";

      if (stageFeeShare) {
        feeAmount =
          Number(matchingInvoice?.amount || 0) > 0
            ? Number(matchingInvoice.amount)
            : Number(stageFeeShare.stage_fee || 0) > 0
            ? Number(stageFeeShare.stage_fee)
            : Number(matchingInvoice?.payable || 0);

        if (courseFeeShare?.share_type === "fixed") {
          shareAmount = Number(stageFeeShare.share_amount || 0);
        } else if (courseFeeShare?.share_type === "percent") {
          shareAmount = Math.round(feeAmount * (Number(stageFeeShare.share_amount || 0) / 100));
        }
      } else if (matchingInvoice) {
        feeAmount = Number(matchingInvoice.amount || matchingInvoice.payable || 0);
        shareAmount = Math.round(feeAmount * 0.5);
      }

      if (matchingPayment) {
        return {
          feeAmount,
          shareAmount: Number(matchingPayment.amount ?? shareAmount),
          shareType,
          paymentStatus: matchingPayment.status || "pending",
          paymentDate: matchingPayment.created_at?.split("T")[0] || new Date().toISOString().split("T")[0],
        };
      }

      return {
        feeAmount,
        shareAmount,
        shareType,
        paymentStatus: "pending",
        paymentDate: new Date().toISOString().split("T")[0],
      };
    }

    return {
      feeAmount: 0,
      shareAmount: 0,
      paymentStatus: "pending",
      paymentDate: new Date().toISOString().split("T")[0],
    };
  };

  // Helper function to calculate estimated share amount from center data
  const calculateEstimatedShare = (
    studentData: any,
    feeType: string,
    feeAmount: number
  ) => {
    const feeTypeObj = feetypes.find((ft) => ft.id.toString() === feeType);
    const feeTypeCode = feeTypeObj?.type || (feeType === "1" ? "coursefee" : feeType === "2" ? "registrationfee" : "");
    const center = studentData?.center;

    if (!center) return Math.round(feeAmount * 0.5);

    const courseId = getCourseId(studentData);
    const stageId = getStageId(studentData);

    const regFeeShareList = center?.reg_fee_share || center?.regFeeShare || [];
    const courseFeeShareList = center?.course_fee_share || center?.courseFeeShare || [];

    if (feeType === "2" || feeTypeCode === "registrationfee") {
      const regFeeShare = regFeeShareList.find(
        (fee: any) => Number(fee.course_id) === Number(courseId)
      );

      if (regFeeShare) {
        if (regFeeShare.share_type === "fixed") {
          return Number(regFeeShare.share_amount || 0);
        } else if (regFeeShare.share_type === "percent") {
          const baseAmount = feeAmount > 0 ? feeAmount : Number(regFeeShare.reg_fee || 0);
          return Math.round(baseAmount * (Number(regFeeShare.share_amount || 0) / 100));
        }
      }
    } else if (feeType === "1" || feeTypeCode === "coursefee") {
      const courseFeeShare = courseFeeShareList.find(
        (fee: any) => Number(fee.course_id) === Number(courseId)
      );

      const stageFeeShareList = courseFeeShare?.stageFeeShare || courseFeeShare?.stage_fee_share || [];
      const stageFeeShare = stageFeeShareList.find((stage: any) =>
        stageId ? Number(stage.stage_id) === Number(stageId) : true
      );

      if (stageFeeShare) {
        if (courseFeeShare?.share_type === "fixed") {
          return Number(stageFeeShare.share_amount || 0);
        } else if (courseFeeShare?.share_type === "percent") {
          const baseAmount = feeAmount > 0 ? feeAmount : Number(stageFeeShare.stage_fee || 0);
          return Math.round(baseAmount * (Number(stageFeeShare.share_amount || 0) / 100));
        }
      }
    }

    return Math.round(feeAmount * 0.5);
  };

  return {
    getFeeAndShareInfo,
    calculateEstimatedShare,
  };
};
